/**
 * The BUILT-IN saved views — Step 17, moved to @itsm/shared at Step 50.
 *
 * They lived in the UI while only the UI read them. Then a dashboard widget
 * started naming a view by id, and the SERVER had to resolve "my-open" to a
 * filter in order to count it — so a built-in that only the browser knew
 * about was a card that could only ever 404.
 *
 * Which is the same reason `buildFieldSchema` and the condition grammar are
 * here: one definition, two runtimes, no drift. A built-in view is not UI, it
 * is a saved query that happens to ship as code.
 *
 * Naming follows the reference's own dashboard — `Scope - What` — after Kai:
 * *"can we remove useless views with the bad naming scheme"*. Migration 0071
 * did the same to the seeded ones and deleted the duplicates of these.
 */
export interface BuiltinView {
  id: string;
  name: string;
  scope: 'private' | 'group' | 'org';
  query: string;
}

const f = (c: unknown) => `filter=${encodeURIComponent(JSON.stringify(c))}`;
const UNRESOLVED = { op: 'in', fieldKey: 'statusCategory', value: ['open', 'pending'] };

export const BUILTIN_VIEWS: BuiltinView[] = [
  {
    id: 'my-open',
    name: 'Assigned to Me - Open',
    scope: 'org',
    query: f({ op: 'and', conditions: [{ op: 'eq', fieldKey: 'assigneeId', value: '@me' }, UNRESOLVED] }),
  },
  {
    id: 'unassigned',
    name: 'Unassigned & Open',
    scope: 'org',
    query: f({
      op: 'and',
      conditions: [
        { op: 'isEmpty', fieldKey: 'assigneeId' },
        { op: 'eq', fieldKey: 'statusCategory', value: 'open' },
      ],
    }),
  },
  { id: 'all-open', name: 'All Unresolved', scope: 'org', query: f(UNRESOLVED) },
  {
    id: 'resolved',
    name: 'Assigned to Me - Recently Resolved',
    scope: 'org',
    query: f({ op: 'eq', fieldKey: 'statusCategory', value: 'resolved' }),
  },
  {
    id: 'my-groups',
    name: 'My Group - All Unresolved',
    scope: 'org',
    query: f({ op: 'and', conditions: [{ op: 'eq', fieldKey: 'groupId', value: '@myGroups' }, UNRESOLVED] }),
  },
  {
    id: 'watching',
    name: "Tickets I'm Watching",
    scope: 'org',
    query: `watching=me&${f(UNRESOLVED)}`,
  },
];

/** A built-in by id, or undefined. The server uses this to resolve a widget
 *  that names one. */
export function builtinView(id: string): BuiltinView | undefined {
  return BUILTIN_VIEWS.find((v) => v.id === id);
}
